import { Injectable } from '@angular/core';
import { Router, NavigationExtras } from "@angular/router";
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { ErrorHandleService } from './error-handle.service';
import { LocalStoreManager } from './local-store-manager.service';
import { EndpointFactory } from './endpoint-factory.service';
import { ConfigurationService } from './configuration.service';
import { DBkeys } from './db-Keys';
import { AccountService } from "./account.service";
import { Utilities } from './utilities';
import { LoginResponse } from '../models/login-response.model';
import { User } from '../models/user.model';

@Injectable()
export class AuthService {

  loginRedirectUrl: string;

  private previousIsLoggedInCheck = false;
  private _loginStatus = new Subject<boolean>();

  reLoginDelegate: () => void;

  constructor(private router: Router, private configurations: ConfigurationService, private accountService: AccountService,
    private errorHandleService: ErrorHandleService, private endpointFactory: EndpointFactory, private localStorage: LocalStoreManager) {
    this.initializeLoginStatus();
  }

  get loginUrl() { return this.configurations.loginUrl; }

  get homeUrl(): string { return this.configurations.homeUrl; }

  get currentUser(): User {

    let user = this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    this.reevaluateLoginStatus(user);

    return user;
  }

  get accessToken(): string {

    this.reevaluateLoginStatus();
    return this.localStorage.getData(DBkeys.ACCESS_TOKEN);
  }

  get accessTokenExpiryDate(): Date {

    this.reevaluateLoginStatus();
    return this.localStorage.getDataObject<Date>(DBkeys.TOKEN_EXPIRES_IN, true);
  }

  get isSessionExpired(): boolean {

    if (this.accessTokenExpiryDate == null) {
      return true;
    }

    return !(this.accessTokenExpiryDate.valueOf() > new Date().valueOf());
  }

  get refreshToken(): string {

    this.reevaluateLoginStatus();
    return this.localStorage.getData(DBkeys.REFRESH_TOKEN);
  }

  get isLoggedIn(): boolean {
    return this.currentUser != null;
  }

  get isVacantBedsReferenceUser(): boolean {
    return this.currentUser != null && this.currentUser.authority.isVacantBedsReference;
  }

  get isVacantBedsEditUser(): boolean {
    return this.currentUser != null && this.currentUser.authority.isVacantBedsEdit;
  }

  get isSaikiTubeReferenceUser(): boolean {
    return this.currentUser != null && this.currentUser.authority.isSaikiTubeReference;
  }

  get isAdmin(): boolean {
    return this.currentUser != null && this.currentUser.authority.isAdmin;
  }

  private initializeLoginStatus() {

    this.localStorage.getInitEvent().subscribe(() => {
      this.reevaluateLoginStatus();
    });
  }

  private processLoginResponse(response: LoginResponse) {

    let accessToken = response.access_token;

    if (accessToken == null)
      throw new Error("アクセストークンが空です。");

    let refreshToken = response.refresh_token || this.refreshToken;
    let expiresIn = response.expires_in;

    let tokenExpiryDate = new Date();
    tokenExpiryDate.setSeconds(tokenExpiryDate.getSeconds() + expiresIn);

    this.saveToken(accessToken, refreshToken, tokenExpiryDate);

    let userid = this.localStorage.getData(DBkeys.CURRENT_USERID);

    return this.accountService.getUser(userid).subscribe(user => {
      this.reevaluateLoginStatus(user);
      this.saveUser(user);
    }, error => this.errorHandleService.showMessageAndRedirect("ログイン失敗", "ユーザー情報の取得に失敗しました。", error));
  }

  private saveToken(accessToken: string, refreshToken: string, expiresIn: Date) {

    this.localStorage.saveSyncedSessionData(accessToken, DBkeys.ACCESS_TOKEN);
    this.localStorage.saveSyncedSessionData(refreshToken, DBkeys.REFRESH_TOKEN);
    this.localStorage.saveSyncedSessionData(expiresIn, DBkeys.TOKEN_EXPIRES_IN);
  }
  private saveUser(user: User) {
    
    this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);
  }

  private reevaluateLoginStatus(currentUser?: User) {

    let user = currentUser || this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    let isLoggedIn = user != null;

    if (this.previousIsLoggedInCheck != isLoggedIn) {
      setTimeout(() => {
        this._loginStatus.next(isLoggedIn);
      });
    }

    this.previousIsLoggedInCheck = isLoggedIn;
  }

  redirectLoginUser() {

    let redirect = this.loginRedirectUrl && this.loginRedirectUrl != '/' && this.loginRedirectUrl != ConfigurationService.defaultHomeUrl ? this.loginRedirectUrl : this.homeUrl;
    this.loginRedirectUrl = null;

    // URL解析
    // [url]?[a=b&c=d]#[fragment]の場合、
    // fragment:[fragment], queryParams: {a:b, c:d}, queryParamsHandling:"merge" のNavigationExtrasつきで[url]にnavigate
    let urlParamsAndFragment = Utilities.splitInTwo(redirect, '#');
    let urlAndParams = Utilities.splitInTwo(urlParamsAndFragment.firstPart, '?');

    let navigationExtras: NavigationExtras = {
      fragment: urlParamsAndFragment.secondPart,
      queryParams: Utilities.getQueryParamsFromString(urlAndParams.secondPart),
      queryParamsHandling: "merge"
    };

    this.router.navigate([urlAndParams.firstPart], navigationExtras);
  }

  login(userId: string, password: string) {

    if (this.isLoggedIn)
      this.logout();

    this.localStorage.saveSessionData(userId, DBkeys.CURRENT_USERID);
    return this.endpointFactory.getLoginEndpoint<LoginResponse>(userId, password).pipe(
      map(response => this.processLoginResponse(response)));
  }

  refreshLogin() {

    return this.endpointFactory.getRefreshLoginEndpoint<LoginResponse>().pipe(
      map(response => this.processLoginResponse(response)));
  }

  reLogin() {

    this.localStorage.deleteData(DBkeys.TOKEN_EXPIRES_IN);

    if (this.reLoginDelegate) {
      this.reLoginDelegate();
    }
    else {
      this.redirectForLogin();
    }
  }

  redirectForLogin() {

    this.loginRedirectUrl = this.router.url;
    this.router.navigate([this.loginUrl]);
  }

  logout(): void {

    this.localStorage.deleteData(DBkeys.ACCESS_TOKEN);
    this.localStorage.deleteData(DBkeys.REFRESH_TOKEN);
    this.localStorage.deleteData(DBkeys.TOKEN_EXPIRES_IN);
    this.localStorage.deleteData(DBkeys.CURRENT_USER);
    this.localStorage.deleteData(DBkeys.CURRENT_USERID);

    this.configurations.clearLocalChanges();
    this.reevaluateLoginStatus();
  }

  getLoginStatusEvent(): Observable<boolean> {

    return this._loginStatus.asObservable();
  }
}
